/* GAH-TAP-01: touch feedback for the modules people already tap.
   One delegated layer, opt-in by attribute, inert until <html> carries
   .gd-tap (GD_FLAGS.tapFx, written by GD_FLAGS.apply() in theme.liquid).
   The arm is read at EVENT time, never at load: Intelligems assigns 2.5-5s
   after first paint, so anything latched at load would miss the arm.

     data-bm-tap="press"     a real control that had no pressed state
                             (no target ships with it today: the bundle
                             tiles were left alone on purpose, Sep 18)
     data-bm-tap="confirm"   a sale chip: tap -> check draws in, the face
                             settles, "Applied" shows beside it for a moment
     data-bm-tap="flash"     a line of copy (the buy-box bullets): tap -> one
                             light sweep across it, nothing else changes
     data-bm-tap-group       chips that settle together
     data-bm-tap-label       the word shown beside the chip (no figures: the
                             chip itself carries the number every arm and
                             market rendered, and this layer never edits it)
     data-bm-tap-name        the target name sent with the gd_tap event
     data-bm-tap-proxy       a row whose taps count as a tap on its chip
     data-bm-tap-chain       a link that ends at a chip (the header sale bar)
     data-bm-tap-follow      a label that settles with a group, not tappable

   The chip's text is never touched. benchmark-deal-price.js, the PCT label
   and Intelligems all rewrite textContent on these nodes; a text swap here
   would race them and could print a figure no arm showed. The check is a
   ::before, the hit area a ::after, and the word lives in one shared
   body-level node so no ancestor's overflow can clip it. */
(() => {
  const ARM_CLASS = 'gd-tap';
  const TARGET = '[data-bm-tap]';
  const APPLIED = 'is-tap-applied';
  const CONFIRMING = 'is-tap-confirming';
  const PRESSED = 'is-tap-pressed';
  const MEASURING = 'is-tap-measuring';
  const CONFIRM_MS = 1600;
  const FLASH_MS = 700;
  const FLASHED = 'is-tap-flashed';
  const PRESS_MIN_MS = 120;
  const CHAIN_KEY = 'gd_tap_chain';
  const CHAIN_MAX_AGE_MS = 15000;
  const NATIVE = 'a[href], button, input, select, textarea, summary, label';

  const root = document.documentElement;
  const armed = () => root.classList.contains(ARM_CLASS);
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let note = null;
  let noteTimer = 0;
  let liveRegion = null;
  const confirmTimers = new WeakMap();

  const track = (el, mode) => {
    const name = el.dataset.bmTapName || 'unnamed';
    if (window.GD_FLAGS && window.GD_FLAGS.track) {
      window.GD_FLAGS.track('gd_tap', { gd_tap_target: name, gd_tap_mode: mode });
    }
  };

  const announce = (text) => {
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'bm-tap-live';
      liveRegion.setAttribute('aria-live', 'polite');
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = '';
    window.setTimeout(() => { liveRegion.textContent = text; }, 50);
  };

  const isFixed = (el) => {
    for (let node = el; node && node !== document.body; node = node.parentElement) {
      if (window.getComputedStyle(node).position === 'fixed') return true;
    }
    return false;
  };

  // where the chip will sit once the check has drawn in. the row decides
  // which way it grows (the buy box grows right, the centred sticky bar grows
  // both ways), so ask the layout instead of assuming: settle it with
  // transitions off, read it, put it back, and the check still animates from
  // zero afterwards
  // the layout box about the painted centre: a chip caught mid-press (.97) or
  // hover-lifted (1.04) paints a pixel or two off its real edges, and the
  // word should sit against the edges it comes to rest on
  const restingRect = (chip) => {
    const painted = chip.getBoundingClientRect();
    const width = chip.offsetWidth;
    const height = chip.offsetHeight;
    const left = painted.left + (painted.width - width) / 2;
    const top = painted.top + (painted.height - height) / 2;
    return { left, top, width, height, right: left + width, bottom: top + height };
  };

  const settledRect = (chip) => {
    if (chip.classList.contains(APPLIED)) return restingRect(chip);
    chip.classList.add(MEASURING, APPLIED);
    const rect = restingRect(chip);
    chip.classList.remove(APPLIED);
    chip.getBoundingClientRect();
    chip.classList.remove(MEASURING);
    return rect;
  };

  const hasOwnText = (el) =>
    Array.from(el.childNodes).some((node) => node.nodeType === 3 && node.textContent.trim());

  // true when the word would land on somebody else's words (the sticky bar
  // keeps "Arrives Sep 22-24" right beside the chip)
  const blocked = (chip, left, top, width, height) => {
    const y = top + height / 2;
    return [left + 1, left + width / 2, left + width - 1].some((x) =>
      document.elementsFromPoint(x, y).some((el) => el !== chip && el !== note && !el.contains(chip) && hasOwnText(el))
    );
  };

  // the word beside the chip, placed at tap time: to the right when that
  // spot is free, otherwise above the chip, otherwise below it
  const showNote = (chip, text, rect) => {
    if (!text) return;
    if (!note) {
      note = document.createElement('span');
      note.className = 'bm-tap-note';
      note.setAttribute('aria-hidden', 'true');
      document.body.appendChild(note);
    }
    window.clearTimeout(noteTimer);
    note.classList.remove('is-visible');
    note.textContent = text;
    const fixed = isFixed(chip);
    note.style.position = fixed ? 'fixed' : 'absolute';
    // in the page it sits under the header, drawers and sheets; only a chip
    // that is itself in a fixed bar needs to clear that bar
    note.style.zIndex = fixed ? '2147483000' : '3';
    const offsetX = fixed ? 0 : window.scrollX;
    const offsetY = fixed ? 0 : window.scrollY;
    const width = note.offsetWidth;
    const height = note.offsetHeight;
    const viewport = document.documentElement.clientWidth;
    const clampX = (x) => Math.max(12, Math.min(x, viewport - 12 - width));
    const spots = [
      { left: rect.right + 8, top: rect.top + (rect.height - height) / 2 },
      { left: clampX(rect.right - width), top: rect.top - height - 6 },
      { left: clampX(rect.right - width), top: rect.bottom + 6 },
    ];
    const free = spots.find((spot) =>
      spot.left + width <= viewport - 12 && !blocked(chip, spot.left, spot.top, width, height)
    );
    const { left, top } = free || spots[1];
    note.style.left = `${Math.round(left + offsetX)}px`;
    note.style.top = `${Math.round(top + offsetY)}px`;
    // next frame, so the fade starts from the new position
    window.requestAnimationFrame(() => note.classList.add('is-visible'));
    noteTimer = window.setTimeout(() => note.classList.remove('is-visible'), CONFIRM_MS);
  };

  const confirm = (chip, source) => {
    if (!chip) return;
    const group = chip.dataset.bmTapGroup;
    const label = chip.dataset.bmTapLabel || '';
    // read BEFORE the state changes, so the check still animates from zero
    showNote(chip, label, settledRect(chip));
    const peers = group
      ? document.querySelectorAll(`[data-bm-tap-group="${group}"], [data-bm-tap-follow="${group}"]`)
      : [chip];
    peers.forEach((peer) => peer.classList.add(APPLIED));
    chip.classList.add(APPLIED, CONFIRMING);
    window.clearTimeout(confirmTimers.get(chip));
    confirmTimers.set(chip, window.setTimeout(() => chip.classList.remove(CONFIRMING), CONFIRM_MS));
    announce(`${chip.textContent.trim()} ${label.toLowerCase()}`.trim());
    track(chip, source || 'confirm');
  };

  // laid out is not the same as seen: GAH-DROP-01 holds .bb-price at
  // visibility:hidden (full rect, nothing painted) until its arm resolves,
  // and a chain must never confirm a chip the visitor cannot see
  const visible = (el) => {
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && window.getComputedStyle(el).visibility !== 'hidden';
  };

  const firstVisibleChip = (group) =>
    Array.from(document.querySelectorAll(`[data-bm-tap="confirm"][data-bm-tap-group="${group}"]`)).find(visible);

  // the header sale bar ends at the buy-box chip: on the PDP the header
  // scrolls there (header-benchmark.js owns that, untouched), anywhere else
  // the link navigates and the chip confirms on arrival
  const runChain = (link) => {
    const group = link.dataset.bmTapChain;
    track(link, 'chain');
    const chip = firstVisibleChip(group);
    if (!chip) {
      // hand off only when the link will really leave the page: on a product
      // template header-benchmark.js swallows the click and scrolls instead,
      // and a handoff stored there would fire on whatever page comes next
      const staysHere = link.hasAttribute('data-scroll-to-buybox') && document.querySelector('[data-buybox]');
      if (staysHere) return;
      try { sessionStorage.setItem(CHAIN_KEY, JSON.stringify({ group, at: Date.now() })); } catch (e) {}
      return;
    }
    // header-benchmark.js has just started a smooth scroll to the buy box.
    // the word is placed where the chip is at tap time, so it must wait for
    // the scroll to stop (Daniel 10-06: "Applied" landed mid-scroll, off the
    // chip), not for a guessed 650ms
    whenSettled(chip, () => confirm(chip, 'chain'));
  };

  // runs fn once the page has stopped moving: scrollY unchanged for four
  // frames, after at least 150ms (a smooth scroll takes a frame to start),
  // or after 2.5s whatever is happening
  const whenSettled = (chip, fn) => {
    if (reducedMotion()) { fn(); return; }
    const started = Date.now();
    let last = window.scrollY;
    let still = 0;
    const tick = () => {
      const now = window.scrollY;
      still = Math.abs(now - last) < 0.5 ? still + 1 : 0;
      last = now;
      const waited = Date.now() - started;
      if ((waited >= 150 && still >= 4) || waited > 2500) { fn(); return; }
      window.requestAnimationFrame(tick);
    };
    window.requestAnimationFrame(tick);
  };

  const flashTimers = new WeakMap();
  const flash = (el) => {
    el.classList.remove(FLASHED);
    // reflow so a second tap restarts the sweep
    el.getBoundingClientRect();
    el.classList.add(FLASHED);
    window.clearTimeout(flashTimers.get(el));
    flashTimers.set(el, window.setTimeout(() => el.classList.remove(FLASHED), FLASH_MS));
    track(el, 'flash');
  };

  const resumeChain = () => {
    let pending = null;
    try {
      pending = JSON.parse(sessionStorage.getItem(CHAIN_KEY) || 'null');
      sessionStorage.removeItem(CHAIN_KEY);
    } catch (e) {}
    if (!pending || Date.now() - pending.at > CHAIN_MAX_AGE_MS) return;
    // the arm can land seconds after paint; wait for it, then give up quietly
    let waited = 0;
    const attempt = () => {
      if (armed()) {
        const chip = firstVisibleChip(pending.group);
        if (chip) confirm(chip, 'chain');
        return;
      }
      waited += 400;
      if (waited <= 6000) window.setTimeout(attempt, 400);
    };
    window.setTimeout(attempt, 700);
  };

  // a span is not a control until the arm says so: role, tab stop and the
  // pointer cursor arrive with the class and leave with it
  let wasArmed = null;
  const syncSemantics = () => {
    const on = armed();
    // <html> changes class for many reasons (scroll lock, other arms); only
    // the arm itself flipping is work
    if (on === wasArmed) return;
    wasArmed = on;
    document.querySelectorAll('[data-bm-tap="confirm"]').forEach((chip) => {
      if (chip.matches(NATIVE)) return;
      if (on) {
        chip.setAttribute('role', 'button');
        chip.setAttribute('tabindex', '0');
      } else {
        chip.removeAttribute('role');
        chip.removeAttribute('tabindex');
        chip.classList.remove(APPLIED, CONFIRMING);
      }
    });
    if (!on) {
      document.querySelectorAll('[data-bm-tap-follow]').forEach((el) => el.classList.remove(APPLIED));
    }
  };

  document.addEventListener('click', (event) => {
    if (!armed() || !event.target.closest) return;
    const link = event.target.closest('[data-bm-tap-chain]');
    if (link) {
      runChain(link);
      return;
    }
    const chip = event.target.closest('[data-bm-tap="confirm"]');
    if (chip) {
      confirm(chip);
      return;
    }
    const row = event.target.closest('[data-bm-tap-proxy]');
    if (row && !event.target.closest(NATIVE)) {
      // GAH-SAVE-01: the row ships a "60% OFF" and a "SAVE $180" chip and CSS
      // shows one, so the proxy confirms the chip on screen, not the first
      const rowChips = Array.from(row.querySelectorAll('[data-bm-tap="confirm"]'));
      confirm(rowChips.find(visible) || rowChips[0], 'proxy');
      return;
    }
    const press = event.target.closest('[data-bm-tap="press"]');
    if (press) {
      track(press, 'press');
      return;
    }
    const line = event.target.closest('[data-bm-tap="flash"]');
    if (line) flash(line);
  });

  document.addEventListener('keydown', (event) => {
    if (!armed() || (event.key !== 'Enter' && event.key !== ' ')) return;
    const chip = event.target.closest && event.target.closest('[data-bm-tap="confirm"]');
    if (!chip || chip.matches(NATIVE)) return;
    event.preventDefault();
    confirm(chip);
  });

  // a class, not :active. iOS only paints :active on a quick tap when a
  // touchstart listener exists, and then drops it before 120ms has passed;
  // the class holds the press long enough to be felt
  const pressedAt = new WeakMap();
  // one live timer per element: a second press inside the first one's window
  // must not be let go by the first one's timer
  const pressTimers = new WeakMap();
  const letGo = (el, wait) => {
    window.clearTimeout(pressTimers.get(el));
    pressTimers.set(el, window.setTimeout(() => el.classList.remove(PRESSED), wait));
  };
  document.addEventListener('pointerdown', (event) => {
    if (!armed() || !event.target.closest) return;
    const el = event.target.closest(TARGET);
    // the word belongs to the last tap; the next touch anywhere else ends it
    if (note && !event.target.closest('[data-bm-tap="confirm"], [data-bm-tap-proxy], [data-bm-tap-chain]')) {
      note.classList.remove('is-visible');
    }
    if (!el) return;
    pressedAt.set(el, Date.now());
    el.classList.add(PRESSED);
    // a pointer released outside the window sends no pointerup; nothing
    // stays pressed past this
    letGo(el, 1500);
  }, { passive: true });

  const release = (event) => {
    document.querySelectorAll(`.${PRESSED}`).forEach((el) => {
      const held = Date.now() - (pressedAt.get(el) || 0);
      // a cancel is the browser taking the gesture for a scroll: let go now
      const wait = event.type === 'pointercancel' ? 0 : Math.max(0, PRESS_MIN_MS - held);
      letGo(el, wait);
    });
  };
  document.addEventListener('pointerup', release, { passive: true });
  document.addEventListener('pointercancel', release, { passive: true });
  window.addEventListener('blur', release);

  new MutationObserver(syncSemantics).observe(root, { attributes: true, attributeFilter: ['class'] });
  // the theme editor swaps a section's nodes in place; the fresh chips need
  // their role back
  document.addEventListener('shopify:section:load', () => {
    wasArmed = null;
    syncSemantics();
  });

  const start = () => {
    syncSemantics();
    resumeChain();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
